import React from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import InputText from "component/inputText/inputText"
import Toggle from "component/toggle/Toggle"
import Select from "component/select/select"
import IconsColor from "util/iconsColor"
import styles from "../../asset/styles/packagesDetail/packagesDetail.module.scss"
import { PackageDetailModel, PackageDayTripEntry } from "./packagesDetailTypes"
import { DayTripItem } from "../transportation/DatTrip"

interface PackageDayTripSelectionProps {
    data: PackageDetailModel
    dayTrips: DayTripItem[]
    onChange: (updates: Partial<PackageDetailModel>) => void
    errors?: { package_name?: string; trips?: string }
}

// const PACKAGE_DAY_TRIP_NOTE =
//     "Note: Day Trip prices are not used in Packages. You will set manual pricing in the next section."

const PackageDayTripSelection: React.FC<PackageDayTripSelectionProps> = ({
    data,
    dayTrips,
    onChange,
    errors = {},
}) => {
    const trips = data.package_day_trip ?? []

    const selectedTripIds = new Set(trips.map((t) => t.day_trip_id).filter(Boolean))

    const baseDayTripOptions = dayTrips.map((dt) => ({
        value: dt.day_trip_id,
        label: dt.flag_active ? dt.day_trip_name : `${dt.day_trip_name}`,
        // Allow already-selected inactive trips to remain; disable unselected inactive ones
        disabled: !dt.flag_active && !selectedTripIds.has(dt.day_trip_id),
    }))

    // Include already-selected trips that may not be in the day trips list at all
    const existingIds = new Set(baseDayTripOptions.map((o) => o.value))
    const missingOptions = trips
        .filter((t) => t.day_trip_id && !existingIds.has(t.day_trip_id))
        .map((t) => ({ value: t.day_trip_id, label: `${t.day_trip_name}`, disabled: false }))

    const dayTripOptions = [...baseDayTripOptions, ...missingOptions]

    const handleAddDayTrip = () => {
        const next: PackageDayTripEntry[] = [
            ...trips,
            { day_trip_id: "", day_trip_name: "" },
        ]
        onChange({ package_day_trip: next })
    }

    const handleTripChange = (index: number, day_trip_id: string) => {
        const entry = dayTripOptions.find((o) => o.value === day_trip_id)
        const name = entry?.label ?? ""
        const next = [...trips]
        if (next[index]) {
            next[index] = { day_trip_id, day_trip_name: name }
            onChange({ package_day_trip: next })
        }
    }

    const handleRemoveTrip = (index: number) => {
        if (trips.length <= 1) return // At least one trip is required; cannot delete all
        const next = trips.filter((_, i) => i !== index)
        onChange({ package_day_trip: next })
    }

    return (
        <div className={utilStyles.pageContainerContent}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Package Details</h2>
                <div className={styles.statusField}>
                    <div className={styles.statusToggle}>
                        <Toggle
                            leftLabel="Inactive"
                            rightLabel="Active"
                            checked={data.flag_active}
                            onChange={(checked) =>
                                onChange({ flag_active: checked })
                            }
                        />
                    </div>
                </div>
            </div>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <InputText
                        label="Package Name"
                        value={data.package_name}
                        onChange={(e) =>
                            onChange({ package_name: e.target.value })
                        }
                        placeholder="Enter package name"
                        required
                        error={errors.package_name}
                    />
                </Grid>
                {trips.length === 0 ? (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <button
                            type="button"
                            className={styles.addDayTripLink}
                            onClick={handleAddDayTrip}
                        >
                            + Add Day Trip
                        </button>
                    </Grid>
                ) : (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <div className={styles.tripRows}>
                            {trips.map((_, index) => (
                                <div key={index} className={styles.tripRow}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                        <Select
                                            label={`Trip ${index + 1}`}
                                            value={trips[index]?.day_trip_id ?? ""}
                                            onChange={(e) =>
                                                handleTripChange(
                                                    index,
                                                    e.target.value
                                                )
                                            }
                                            options={dayTripOptions}
                                            placeholder={`Select Trip ${index + 1}`}
                                            error={errors.trips}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTrip(index)}
                                        disabled={trips.length <= 1}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: trips.length <= 1 ? "not-allowed" : "pointer",
                                            padding: 0,
                                            display: "inline-flex",
                                            color: trips.length <= 1 ? "#9e9e9e" : "#d32f2f",
                                            marginTop: 24,
                                            opacity: trips.length <= 1 ? 0.6 : 1,
                                        }}
                                        aria-label="Remove trip"
                                        title={trips.length <= 1 ? "At least one trip is required" : "Remove trip"}
                                    >
                                        <IconsColor
                                            iconName="deleteIcon"
                                            width="30px"
                                            height="30px"
                                            fillColor="none"
                                            strokeColor="currentColor"
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            className={styles.addDayTripLink}
                            onClick={handleAddDayTrip}
                        >
                            + Add Day Trip
                        </button>
                    </Grid>
                )}
            </Grid>
        </div>
    )
}

export default PackageDayTripSelection
